import React, { useEffect } from 'react';
import { useStore, useStoreMap } from 'effector-react';
import { Scrollbars } from 'react-custom-scrollbars';
import produce from 'immer';
import omit from 'ramda/es/omit';
import {
  AutoRotateAction,
  AutoRotateEndOperation,
  AutoRotateType,
  type BooleanFlag,
  SceneType,
  VideoLoopType,
  VoiceLoopType,
} from '@types';
import { $loadedScene } from '@stores/loadedStore';
import { CollapseWrapper } from '@components/Collapse';
import { $sceneMapApi, $tourData } from '@stores/dataStores';
import Title from '@components/Title';
import { sendSuccess } from '@components/Noti';
import { SidebarAutoRotatePano } from './SidebarAutoRotatePano';
import SidebarHeader from '../../sidebar/components/SidebarHeader';
import SidebarApplyTo from '../../sidebar/components/SidebarApplyTo';
import { showSceneModal } from '../../category';
import type { SidebarAutoRotateProps } from './types';
import { SidebarAutoRotatePVideo } from './SidebarAutoRotatePVideo';
import { SidebarAutoRotateMatrix } from './SidebarAutoRotateMatrix';
import { VoiceSetting } from './VoiceSetting';
import { EndOperationSetting } from './EndOperationSetting';
import { SubtitleSetting } from './SubtitleSetting';

const getRotateAction = (sceneType: SceneType, autoNext?: BooleanFlag) => {
  if (sceneType === SceneType.pano) {
    return autoNext === 0 ? AutoRotateAction.loop : AutoRotateAction.byRound;
  }
  if (sceneType === SceneType.panoVideo) {
    return AutoRotateAction.byRound;
  }
  if (sceneType === SceneType.matrix) {
    return AutoRotateAction.byTime;
  }
};

export function SidebarAutoRotate() {
  const scene = useStore($loadedScene);
  const { duration: origDur, autoEnterNext: origAutoNext } = useStoreMap({
    store: $tourData,
    keys: [],
    fn: (state) => state.autoRotate ?? {},
  });
  useEffect(() => {
    // v5.3.1 老数据处理
    if (!scene) return;
    let autoRotate = scene.config.autoRotate ?? {
      type:
        scene.type === SceneType.pano
          ? AutoRotateType.right
          : AutoRotateType.none,
      duration: origDur ?? 180,
      stayTime: scene.type === SceneType.matrix ? 30 : 180,
      holdView: 1,
      rotateAction: getRotateAction(scene.type, origAutoNext),
    };
    if (autoRotate.endOperation === undefined) {
      autoRotate = produce(autoRotate, (draft) => {
        if (scene.voice.mediaUrl) {
          draft.endOperation =
            scene.voice.isLoop === VoiceLoopType.switch
              ? AutoRotateEndOperation.byVoice
              : AutoRotateEndOperation.loop;
        } else if (scene.video.length) {
          draft.endOperation =
            scene.video[0].playSetting === VideoLoopType.switch
              ? AutoRotateEndOperation.byVoice
              : AutoRotateEndOperation.loop;
          if (scene.video[0].subtitle) {
            draft.subtitle = scene.video[0].subtitle;
            draft.subtitleFileName = `${scene.video[0].mediaName}.srt`;
          }
        } else if (draft.rotateAction === AutoRotateAction.loop) {
          draft.endOperation = AutoRotateEndOperation.loop;
        } else {
          draft.endOperation = AutoRotateEndOperation.byTour;
        }
      });
    }
    $sceneMapApi.updateScene({
      id: scene.id,
      updater: (draft) => {
        draft.config.autoRotate = autoRotate;
      },
    });
  }, []);
  // v5.3.1 数据更新后再渲染
  if (scene?.config.autoRotate?.endOperation === undefined) return null;

  const handleApplyTo = async () => {
    const { idSet } = await showSceneModal({
      title: '选择要应用到的场景',
      subTitle: '注：只能应用到同类型场景中',
      allowedTypes: [scene.type],
      multiple: true,
    });
    if (!idSet) return;
    if (
      scene.type === SceneType.matrix &&
      scene.config.autoRotate?.type === AutoRotateType.custom
    ) {
      // 矩阵并且导览方式自定义时, 不包含导览方式部分的数据
      const autoRotate = omit(
        ['type', 'duration', 'stayTime', 'holdView', 'tourData'],
        scene.config.autoRotate
      );
      $sceneMapApi.update((draft) => {
        Array.from(idSet).forEach((_id) => {
          draft.get(_id)!.config.autoRotate = {
            ...draft.get(_id)!.config.autoRotate,
            ...autoRotate,
            targetSceneId:
              scene.config.autoRotate?.targetSceneId === _id
                ? ''
                : scene.config.autoRotate?.targetSceneId,
          };
          draft.get(_id)!.voice = scene.voice;
          draft.get(_id)!.video = scene.video;
        });
        sendSuccess('导览讲解已应用到所选场景');
      });
    } else {
      $sceneMapApi.update((draft) => {
        Array.from(idSet).forEach((_id) => {
          draft.get(_id)!.config.autoRotate = {
            ...scene.config.autoRotate,
            targetSceneId:
              scene.config.autoRotate?.targetSceneId === _id
                ? ''
                : scene.config.autoRotate?.targetSceneId,
          };
          draft.get(_id)!.voice = scene.voice;
          draft.get(_id)!.video = scene.video;
        });
        sendSuccess('导览讲解已应用到所选场景');
      });
    }
  };

  const props: SidebarAutoRotateProps = {
    sceneId: scene.id,
    sceneView: scene.view,
    data: scene.config.autoRotate,
  };

  return (
    <Scrollbars>
      <CollapseWrapper>
        <SidebarHeader title={<Title>导览讲解</Title>} />
        {scene.type === SceneType.pano && <SidebarAutoRotatePano {...props} />}
        {scene.type === SceneType.panoVideo && (
          <SidebarAutoRotatePVideo {...props} />
        )}
        {scene.type === SceneType.matrix && (
          <SidebarAutoRotateMatrix {...props} />
        )}
        <VoiceSetting />
        <SubtitleSetting />
        <EndOperationSetting />
        <SidebarApplyTo
          className="mt-10"
          title="把全部导览讲解设置应用到..."
          onClick={handleApplyTo}
        />
      </CollapseWrapper>
    </Scrollbars>
  );
}
